import { Controller } from '@hotwired/stimulus'

// Opens the main modal with content loaded into its turbo frame. Static dialogs open and close
// through command="show-modal" and command="close" buttons instead and do not need this.
export default class extends Controller<HTMLElement> {
  static targets = ['dialog', 'turboframe']

  declare readonly dialogTarget: HTMLDialogElement
  declare readonly turboframeTarget: HTMLElement
  declare readonly hasTurboframeTarget: boolean

  connect (): void {
    if (!this.hasTurboframeTarget) return

    const url = new URL(window.location.href)
    const path = url.searchParams.get('open')
    if (!path?.startsWith('/') || path.startsWith('//')) return

    url.searchParams.delete('open')
    history.replaceState(history.state, '', url)
    this.turboframeTarget.setAttribute('src', path)
    this.show()
  }

  open (event: Event): void {
    const trigger = event.currentTarget as HTMLElement
    const href = (trigger as HTMLAnchorElement).href || trigger.dataset.href

    if (href) {
      event.preventDefault()
      event.stopPropagation()
      if (this.hasTurboframeTarget) this.turboframeTarget.setAttribute('src', href)
    }

    this.show()
  }

  show (): void {
    if (!this.dialogTarget.open) this.dialogTarget.showModal()
  }

  hide (): void {
    this.dialogTarget.close()
  }

  hideIfSuccess (event: CustomEvent): void {
    if (event.detail.success === true) this.hide()
  }

  hideOnSuccess (event: Event): void {
    const button = event.currentTarget as HTMLButtonElement
    button.form?.addEventListener('turbo:submit-end', (e) => this.hideIfSuccess(e as CustomEvent), { once: true })
  }

  dialogTargetConnected (dialog: HTMLDialogElement): void {
    dialog.addEventListener('close', () => this.resetTurboframe())
  }

  // Clear the frame so the next open loads fresh content instead of showing the last dialog.
  private resetTurboframe (): void {
    if (!this.hasTurboframeTarget) return
    this.turboframeTarget.innerHTML = ''
    this.turboframeTarget.removeAttribute('src')
  }
}
